import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../ui/Modal';
import { useNotifications } from '../NotificationSystem';
import { useOrg } from '../../hooks/useOrg';
import {
  uploadLetterhead,
  deleteLetterhead,
  getLetterheadUrl
} from '../../services/letterheadService';

const MAX_BYTES = 5 * 1024 * 1024;

const LetterheadManager = () => {
  const { t } = useTranslation(['qms', 'common']);
  const { notify } = useNotifications();
  const {
    activeOrgId,
    activeOrg,
    isLoading,
    setActiveOrg
  } = useOrg();

  const [previewUrl, setPreviewUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const fromContext = activeOrg?.letterheadImageUrl;
    if (fromContext) {
      setPreviewUrl(fromContext);
      return;
    }
    let cancelled = false;
    (async () => {
      if (!activeOrgId) {
        if (!cancelled) setPreviewUrl(null);
        return;
      }
      const url = await getLetterheadUrl(activeOrgId);
      if (!cancelled) setPreviewUrl(url);
    })();
    return () => {
      cancelled = true;
    };
  }, [activeOrgId, activeOrg?.letterheadImageUrl]);

  const disabled = isLoading || busy || !activeOrgId;

  const refreshOrgContext = async () => {
    if (activeOrgId && typeof setActiveOrg === 'function') {
      await setActiveOrg(activeOrgId);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !activeOrgId) return;
    if (file.size > MAX_BYTES) {
      notify.error(t('qms:letterheadFileTooLarge'));
      return;
    }
    setBusy(true);
    try {
      const url = await uploadLetterhead(activeOrgId, file);
      setPreviewUrl(url);
      await refreshOrgContext();
      notify.success(t('qms:letterheadUploaded'));
    } catch (err) {
      if (err?.message === 'FILE_TOO_LARGE') {
        notify.error(t('qms:letterheadFileTooLarge'));
      } else if (err?.message === 'INVALID_TYPE') {
        notify.error(t('common:error'));
      } else {
        notify.error(err?.message || String(err));
      }
    } finally {
      setBusy(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!activeOrgId) return;
    setBusy(true);
    try {
      await deleteLetterhead(activeOrgId);
      setPreviewUrl(null);
      setShowDeleteModal(false);
      await refreshOrgContext();
      notify.success(t('qms:letterheadDeleted'));
    } catch (err) {
      notify.error(err?.message || String(err));
    } finally {
      setBusy(false);
    }
  };

  const cardStyle = {
    maxWidth: 640,
    padding: 20,
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    background: '#fff'
  };

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ marginTop: 0 }}>{t('qms:letterheadManager')}</h2>
      <p style={{ color: '#6b7280', marginBottom: 20 }}>
        {t('qms:letterheadDescription')}
      </p>

      {!activeOrgId && !isLoading && (
        <p role="alert" style={{ color: '#b45309' }}>
          {t('common:organizationalScopeNoActiveOrg')}
        </p>
      )}

      <div style={cardStyle}>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/jpg"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        {previewUrl ? (
          <>
            <div style={{ marginBottom: 16 }}>
              <img
                src={previewUrl}
                alt=""
                style={{
                  maxHeight: 120,
                  width: 'auto',
                  maxWidth: '100%',
                  display: 'block',
                  objectFit: 'contain'
                }}
              />
            </div>
            <button
              type="button"
              className="btn btn-primary"
              disabled={disabled}
              onClick={() => fileInputRef.current?.click()}
            >
              {t('qms:uploadLetterhead')}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              style={{ marginLeft: 8 }}
              disabled={disabled}
              onClick={() => setShowDeleteModal(true)}
            >
              {t('qms:deleteLetterhead')}
            </button>
          </>
        ) : (
          <>
            <p style={{ color: '#6b7280', marginBottom: 12 }}>
              {t('qms:noLetterheadConfigured')}
            </p>
            <button
              type="button"
              className="btn btn-primary"
              disabled={disabled}
              onClick={() => fileInputRef.current?.click()}
            >
              {t('qms:uploadLetterhead')}
            </button>
          </>
        )}
      </div>

      <Modal
        isOpen={showDeleteModal}
        onClose={() => !busy && setShowDeleteModal(false)}
        title={t('qms:confirmDeleteLetterhead')}
        size="medium"
      >
        <p style={{ marginBottom: 16 }}>{t('qms:confirmDeleteLetterheadBody')}</p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={busy}
            onClick={() => setShowDeleteModal(false)}
          >
            {t('common:cancel')}
          </button>
          <button
            type="button"
            className="btn btn-danger"
            disabled={busy}
            onClick={handleConfirmDelete}
          >
            {t('qms:deleteLetterhead')}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default LetterheadManager;
